import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { t } from 'ttag';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import classNames from 'classnames';

import { FeedFilterTitle } from '@components/FeedFilterTitle';
import { Checkbox } from '@ui/Checkbox';
import { Input } from '@ui/Input';
import { Button } from '@ui/Button';
import {
  useIndexedFilters,
} from '@hook/productFilters';

import s from './FeedBrandFilterNew.sss';

export interface Option {
  id: number,
  name: string,
  isUkrainian?: boolean | null,
  isIndexed?: boolean
}

export interface FeedBrandFilterNewProps {
  className?: string,
  defaultOptions: {
    topBrands: Option[],
    brands: Option[],
  },
  suggests: Option[],
  value: Option[],
  inputValue: string,
  onChange: (brand: Option) => void,
  onInputChange: (value: string) => void,
}

export const FeedBrandFilterNew: React.FC<FeedBrandFilterNewProps> = ({
  className,
  defaultOptions,
  suggests,
  inputValue,
  value = [],
  onChange,
  onInputChange,
}) => {
  const history = useHistory<GlobalLocationState>();
  const [isHide, setHide] = useState(false);
  const { generateIndexedFilterPath } = useIndexedFilters();

  const selectedBrandsOptions = differenceWith(value, defaultOptions.topBrands, isEqual);
  const resultBrandsOptions = uniqBy(defaultOptions.brands.concat(selectedBrandsOptions), 'id');
  const resultSuggestsOptions = uniqBy(suggests.concat(value), 'id');

  const brandsGroupsMap: Map<string, Option[]> = !inputValue && resultBrandsOptions.length ? (
    resultBrandsOptions.reduce<Map<string, Option[]>>((acc, item) => {
      const label = item.name[0].toUpperCase();
      acc.set(label, acc.has(label) ? acc.get(label)!.concat(item) : [item]);
      return acc;
    }, new Map())
  ) : new Map();

  const brandsGroups = Array.from(brandsGroupsMap.entries());
  const isBrandFilterOpened = history.location.state?.isBrandFilterOpened;

  const handleExpand = () => {
    history.replace({
      ...history.location,
      state: {
        isBrandFilterOpened: !isBrandFilterOpened,
      },
    });
  };

  const renderOption = (option: Option, isBold?: boolean) => {
    return (
      <Button
        className={s.optionButton}
        theme='text'
        {...(option.isIndexed && {
          to: generateIndexedFilterPath({ brands: [option.id] }),
        })}
        onClick={(e) => {
          e.preventDefault();

          onChange(option);
        }}
      >
        <Checkbox
          checked={value.some((active) => active.id === option.id)}
          className={classNames(s.checkbox, { [s.ua]: option.isUkrainian })}
        >
        <span
          className={classNames(s.label, {
            [s.labelTypeBold]: isBold,
          })}
        >
          {option.name}
        </span>
        </Checkbox>
      </Button>
    );
  };

  return (
    (
      <div className={classNames(s.root, className)}>
        <FeedFilterTitle
          title={t`Бренд`}
          hide={isHide}
          onClick={() => setHide(!isHide)}
        />
        {!isHide && (
        <>
          <Input
            className={s.input}
            placeholder={t`Пошук`}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
          />

          <div className={s.holder}>
            <ul className={s.list}>
              {[...(inputValue ? resultSuggestsOptions : defaultOptions.topBrands)]
                .map((option, i) => (
                  <li
                    key={i}
                    className={s.item}
                  >
                    {renderOption(option, !inputValue)}
                  </li>
                ))}
            </ul>

            {!!brandsGroups.length && (
            <ul className={s.list}>
              {brandsGroups
                .slice(0, isBrandFilterOpened ? brandsGroups.length : 1)
                .map(([group, groupOptions], i) => (
                  <li
                    key={i}
                    className={s.item}
                  >
                    <p className={s.group}>
                      {group}
                    </p>

                    <ul className={s.list}>
                      {groupOptions.map((option, j) => (
                        <li
                          key={j}
                          className={s.item}
                        >
                          {renderOption(option)}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
            </ul>
            )}
          </div>

          {!!brandsGroups.length && !isBrandFilterOpened && (
          <Button
            className={s.button}
            theme="text"
            onClick={handleExpand}
          >
            {t`Показати все`}
          </Button>
          )}
        </>
        )}
      </div>
    )
  );
};
